<template>
  <b-modal
    :visible="value"
    :title="entity.modalTitle"
    size="lg"
    centered
    no-close-on-backdrop
    dialog-class="cost-gl-modal"
    @hidden="onHidden"
    @change="$emit('input', $event)"
  >
    <b-form-group
      v-for="row in rows"
      :key="row.label"
      label-cols="4"
      label-align="right"
      :label="row.label"
      label-class="font-weight-bold"
    >
      <div
        v-if="row.kind === 'df'"
        class="d-flex align-items-center"
      >
        <b-input-group>
          <b-form-input :value="row.value" disabled />
          <b-input-group-append>
            <b-input-group-text>{{ row.dfType === 0 ? "%" : "บาท" }}</b-input-group-text>
          </b-input-group-append>
        </b-input-group>
        <b-form-radio-group
          :checked="row.dfType"
          disabled
          class="ml-3 text-nowrap"
          :options="[
            { text: 'Percent', value: 0 },
            { text: 'Fixed', value: 1 },
          ]"
        />
      </div>
      <b-input-group v-else>
        <b-form-input :value="row.value" disabled />
        <b-input-group-append v-if="row.suffix">
          <b-input-group-text>{{ row.suffix }}</b-input-group-text>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>

    <template v-if="entity.hasLinkedProducts">
      <hr />
      <h5 class="font-weight-bold mb-0">ผูกเข้ากับคลัง</h5>
      <p class="text-muted">
        สินค้าที่ต้องถูกตัดสต๊อกเมื่อมีการใช้งาน
      </p>
      <b-table-simple
        small
        bordered
        class="linked-products"
      >
        <b-thead head-variant="light">
          <b-tr>
            <b-th>รหัสสินค้า - ชื่อสินค้า</b-th>
            <b-th class="text-right">จำนวนสินค้า</b-th>
            <b-th>หน่วยสินค้า</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr
            v-for="product in products"
            :key="product.productId"
          >
            <b-td>{{ product.no }} - {{ product.description }}</b-td>
            <b-td class="text-right">{{ product.qty }}</b-td>
            <b-td>{{ product.uom || "-" }}</b-td>
          </b-tr>
          <b-tr v-if="products.length === 0">
            <b-td
              colspan="3"
              class="text-center text-muted"
              >ไม่มีสินค้าที่ผูกกับคลัง</b-td
            >
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </template>

    <hr />
    <b-form-group
      label-cols="4"
      label-align="right"
      label="Cost G/L:"
      label-class="font-weight-bold"
      :state="invalid ? false : null"
      :invalid-feedback="'กรุณาเลือก Cost G/L'"
    >
      <!-- searchable by code or name: the option label is "code - name" -->
      <multiselect
        v-model="selectedAccount"
        :options="accounts"
        :custom-label="formatAccount"
        track-by="id"
        placeholder="เลือก Cost G/L (พิมพ์ค้นหารหัสหรือชื่อ)"
        :allow-empty="false"
        select-label=""
        selected-label=""
        deselect-label=""
        class="cost-gl-multiselect"
        :class="{ 'is-invalid': invalid }"
        data-testid="cost-gl-select"
      >
        <span slot="noResult">ไม่พบ Cost G/L</span>
        <span slot="noOptions">ไม่มีรายการ Cost G/L</span>
      </multiselect>
    </b-form-group>

    <template #modal-footer>
      <b-button
        variant="primary"
        class="px-5"
        :disabled="saving"
        data-testid="cost-gl-confirm"
        @click="confirm"
      >
        ยืนยัน
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import Multiselect from "vue-multiselect";
import { buildModalRows, formatAccount } from "@/utils/costGl";

// Everything but the Cost G/L is read-only: the accountant only classifies
// a master that the clinic created.
export default {
  name: "CostGlAssignModal",
  components: { Multiselect },
  props: {
    value: { type: Boolean, default: false },
    entity: { type: Object, required: true },
    item: { type: Object, default: null },
    accounts: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
  },
  data() {
    return { selectedId: null, submitted: false };
  },
  computed: {
    rows() {
      return buildModalRows(this.entity.key, this.item);
    },
    products() {
      return (this.item && this.item.products) || [];
    },
    invalid() {
      return this.submitted && !this.selectedId;
    },
    // vue-multiselect works with the option object; the rest of the modal
    // (and the emitted value) only needs its id
    selectedAccount: {
      get() {
        return this.accounts.find(a => a.id === this.selectedId) || null;
      },
      set(account) {
        this.selectedId = account ? account.id : null;
      },
    },
  },
  watch: {
    // preselect the current Cost G/L when editing a พร้อมใช้งาน row
    item: {
      immediate: true,
      handler(item) {
        this.selectedId = (item && item.costGlId) || null;
        this.submitted = false;
      },
    },
    value(open) {
      if (open) this.submitted = false;
    },
  },
  methods: {
    formatAccount,
    confirm() {
      this.submitted = true;
      if (!this.selectedId) return;
      this.$emit("confirm", this.selectedId);
    },
    onHidden() {
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.cost-gl-multiselect >>> .multiselect__tags {
  background-color: #ebf3fd;
}
.cost-gl-multiselect.is-invalid >>> .multiselect__tags {
  border-color: #dc3545;
}
.linked-products {
  background: #fff;
}
</style>
